/* eslint-disable @typescript-eslint/no-unused-vars */
// Interfaces
import {
  PlusOutlined,
  FileTextOutlined,
  BankOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  BankFilled,
  AppstoreFilled,
  SearchOutlined
} from '@ant-design/icons';

// Constants
import { PRIMARY_COLOR } from '../../../constants/colors';
import { ROUTES } from '../../../constants/routes-constants';

// Interfaces
import type { IUser } from '../../../interfaces/user';
import type { ProLayoutProps } from '@ant-design/pro-layout';

const getGuiasRoutes = (
  _isMobile: boolean,
  permissions: string[],
  _user: IUser
): NonNullable<ProLayoutProps['route']> | null => {
  const hasEstablishments =
    _user.productores_mineros.filter((a) => a.estado === 'activo').length > 0 ||
    _user.no_productores.filter(
      (p) => p.__establecimientos_no_productor__.filter((e) => e.estado === 'activo').length
    ).length > 0;

  return {
    key: 'guias',
    name: 'Guías',
    disabled: !hasEstablishments,
    icon: <FileTextOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      {
        exact: true,
        path: ROUTES.USUARIO_GUIAS_EMITIDAS_ROUTE,
        name: 'Ver mis guías emitidas',
        icon: <FileSearchOutlined />
      },
      {
        path: ROUTES.GUIA_EMISION_ROUTE,
        name: 'Emitir nueva guía',
        icon: <PlusOutlined />
      }
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getEstablecimientosRoutes = (
  _isMobile: boolean,
  permissions: string[],
  _user: IUser
): NonNullable<ProLayoutProps['route']> | null => {
  return {
    key: 'establecimientos',
    name: 'Establecimientos',
    icon: <BankOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      {
        exact: true,
        path: ROUTES.USUARIO_ESTABLECIMIENTOS_ROUTE,
        name: 'Ver mis establecimientos',
        icon: <BankFilled />
      },
      _user.no_productores.length && {
        path: ROUTES.USUARIO_ESTABLECIMIENTOS_NUEVO_ROUTE,
        name: 'Agregar establecimiento',
        icon: <PlusOutlined />
      }
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getCateosRoutes = (
  _isMobile: boolean,
  permissions: string[],
  _user: IUser
): NonNullable<ProLayoutProps['route']> | null => {
  const hasEstablishments =
    _user.productores_mineros.filter((a) => a.estado === 'activo').length > 0 ||
    _user.no_productores.filter(
      (p) => p.__establecimientos_no_productor__.filter((e) => e.estado === 'activo').length
    ).length > 0;

  return {
    key: 'cateos',
    name: 'Cateos',
    disabled: !hasEstablishments,
    icon: <SearchOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      {
        exact: true,
        path: ROUTES.USUARIO_CATEOS_ROUTE,
        name: 'Ver mis cateos',
        icon: <SearchOutlined />
      }
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

const getModulosRoutes = (
  _isMobile: boolean,
  permissions: string[],
  _user: IUser
): NonNullable<ProLayoutProps['route']> | null => {
  const hasEstablishments =
    _user.productores_mineros.filter((a) => a.estado === 'activo').length > 0 ||
    _user.no_productores.filter(
      (p) => p.__establecimientos_no_productor__.filter((e) => e.estado === 'activo').length
    ).length > 0;

  return {
    key: 'modulos',
    name: 'Módulos',
    disabled: !hasEstablishments,
    icon: <AppstoreOutlined style={{ color: PRIMARY_COLOR }} />,
    children: [
      {
        exact: true,
        path: ROUTES.USUARIO_MODULOS_ROUTE,
        name: 'Ver mis módulos',
        icon: <AppstoreFilled />
      },
      {
        path: ROUTES.USUARIO_MODULOS_NUEVO_ROUTE,
        name: 'Comprar módulos',
        icon: <PlusOutlined />
      }
    ].filter(Boolean) as NonNullable<ProLayoutProps['route']>[]
  };
};

export default (_isMobile: boolean, _user: IUser) => {
  const routes: ProLayoutProps['route'][] = [];

  if (
    !_user ||
    (!_user.productores_mineros.filter((a) => a.estado === 'activo').length &&
      !_user.no_productores.length)
  )
    return { routes };

  const permissions =
    (_user && _user?.roles.map((r) => r.permissions.map((p) => p.descripcion)).flat()) || [];

  // Guías Routes
  const guiasRoutes = getGuiasRoutes(_isMobile, permissions, _user);
  if (guiasRoutes) routes.push(guiasRoutes);

  // Establecimientos Routes
  const establecimientosRoutes = getEstablecimientosRoutes(_isMobile, permissions, _user);
  if (establecimientosRoutes) routes.push(establecimientosRoutes);

  // Cateos Routes
  if (_user.productores_mineros.length) {
    const cateosRoutes = getCateosRoutes(_isMobile, permissions, _user);
    if (cateosRoutes) routes.push(cateosRoutes);
  }

  // Módulos Routes
  const modulosRoutes = getModulosRoutes(_isMobile, permissions, _user);
  if (modulosRoutes) routes.push(modulosRoutes);

  return {
    routes
  };
};
